'use client';

import { useState, useEffect, useRef, useMemo, useActionState } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Eye, EyeOff, Mail, Lock, ArrowRight } from '@/lib/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { base44 } from '@/api/base44Client';
import SmartImage from '@/components/ui/SmartImage';
import { login, signup, resendVerification } from './actions';

const LOGO =
  'https://media.base44.com/images/public/69c30ea63e3e77ff647cca18/88795e92e_octne-full-logo-w.png';

const CARD_CONFIGS = [
  { left: 4, top: 8, rot: -2.1, tier: 'back', drift: 0, delay: 0 },
  { left: 32, top: 4, rot: 1.8, tier: 'mid', drift: 1, delay: 3 },
  { left: 58, top: 7, rot: -1.2, tier: 'back', drift: 2, delay: 7 },
  { left: 6, top: 34, rot: 2.5, tier: 'mid', drift: 3, delay: 1 },
  { left: 26, top: 30, rot: -1.8, tier: 'front', drift: 4, delay: 5 },
  { left: 52, top: 28, rot: 1.4, tier: 'front', drift: 5, delay: 2 },
  { left: 72, top: 22, rot: -2.3, tier: 'mid', drift: 0, delay: 9 },
  { left: 3, top: 60, rot: 1.6, tier: 'front', drift: 1, delay: 4 },
  { left: 24, top: 58, rot: -2.8, tier: 'back', drift: 2, delay: 8 },
  { left: 50, top: 56, rot: 2.1, tier: 'mid', drift: 3, delay: 6 },
  { left: 68, top: 52, rot: -1.5, tier: 'front', drift: 4, delay: 11 },
  { left: 18, top: 80, rot: 1.9, tier: 'back', drift: 5, delay: 3 },
];

const TIER_STYLES = {
  front: { opacity: 1, blur: 0, width: [210, 240] },
  mid: { opacity: 0.78, blur: 0, width: [170, 220] },
  back: { opacity: 0.4, blur: 1, width: [130, 160] },
};

const DRIFT_KEYFRAMES = ['drift0', 'drift1', 'drift2', 'drift3', 'drift4', 'drift5'];

const CARD_WIDTHS = CARD_CONFIGS.map((c, i) => {
  const [min, max] = TIER_STYLES[c.tier].width;
  return min + ((i * 37) % (max - min + 1));
});

function FloatingCard({ config, idx, cardWidth, post, focused, cloudCenter }) {
  const tier = TIER_STYLES[config.tier];
  const cardRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    if (focused) {
      vid.currentTime = 0;
      vid.play().catch(() => {});
    } else {
      vid.pause();
    }
  }, [focused]);

  let focusTranslate = { x: 0, y: 0 };
  if (focused && cardRef.current) {
    const rect = cardRef.current.getBoundingClientRect();
    const cardCX = rect.left + rect.width / 2;
    const cardCY = rect.top + rect.height / 2;
    focusTranslate = {
      x: (cloudCenter.x - cardCX) * 0.5,
      y: (cloudCenter.y - cardCY) * 0.5,
    };
  }

  const baseStyle = {
    position: 'absolute',
    left: `${config.left}%`,
    top: `${config.top}%`,
    width: `${cardWidth}px`,
    opacity: focused ? 1 : tier.opacity,
    filter: tier.blur > 0 && !focused ? `blur(${tier.blur}px)` : 'none',
    borderRadius: '14px',
    overflow: 'hidden',
    cursor: 'default',
    willChange: 'transform',
    zIndex: focused ? 40 : config.tier === 'front' ? 10 : config.tier === 'mid' ? 5 : 1,
    transition:
      'transform 0.55s cubic-bezier(0.34,1.4,0.4,1), opacity 0.4s ease, box-shadow 0.4s ease',
    transform: focused
      ? `translate(${focusTranslate.x}px, ${focusTranslate.y}px) rotate(0deg) scale(1.6)`
      : `rotate(${config.rot}deg) scale(1)`,
    boxShadow: focused
      ? '0 24px 80px rgba(0,0,0,0.6), 0 4px 20px rgba(255,91,20,0.2)'
      : '0 8px 24px rgba(0,0,0,0.4)',
    animationName: focused ? 'none' : DRIFT_KEYFRAMES[config.drift],
    animationDuration: `${28 + config.drift * 2}s`,
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    animationDelay: `-${config.delay}s`,
  };

  const allMedia = post?.media_urls || [];
  const videoUrl = allMedia.find((u) => /\.(mp4|mov|webm|ogg)(\?|$)/i.test(u));
  const imgUrl =
    allMedia.find((u) => /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(u)) ||
    post?.before_image ||
    post?.after_image ||
    post?.cover_image;
  const isTextPost =
    !videoUrl && !imgUrl && (post?.post_type === 'text' || post?.post_type === 'poll');
  const hasInfo = focused && post;

  return (
    <div ref={cardRef} style={baseStyle}>
      <div
        style={{
          width: '100%',
          paddingBottom: '100%',
          position: 'relative',
          background: 'hsl(var(--card))',
        }}
      >
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            muted
            loop
            playsInline
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : imgUrl ? (
          <SmartImage
            src={imgUrl}
            alt=""
            width={640}
            sizes="(min-width: 640px) 33vw, 100vw"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : isTextPost ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `hsl(${(idx * 47 + 200) % 360} 35% 15%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
            }}
          >
            <p
              style={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.4,
                textAlign: 'center',
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {post?.poll_question || post?.caption || ''}
            </p>
          </div>
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `hsl(${(idx * 47 + 200) % 360} 40% 18%)`,
            }}
          />
        )}

        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: focused
              ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)'
              : 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)',
            transition: 'background 0.4s ease',
          }}
        />

        {hasInfo && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '12px',
              transform: 'translateY(0)',
              opacity: 1,
              transition: 'opacity 0.3s ease 0.3s, transform 0.3s ease 0.3s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {post.author_avatar ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={post.author_avatar}
                  alt=""
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '1.5px solid rgba(255,255,255,0.4)',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'hsl(var(--primary))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                  }}
                >
                  {(post.author_name || '?')[0]}
                </div>
              )}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {post.author_username ? post.author_username : post.author_name}
              </span>
            </div>
            {(post.car_make || post.car_year) && (
              <p
                style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.7)',
                  marginTop: 2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {[post.car_year, post.car_make, post.car_model].filter(Boolean).join(' ')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FloatingCloud({ posts }) {
  const [focusedIdx, setFocusedIdx] = useState(null);
  const cloudRef = useRef(null);
  const [cloudCenter, setCloudCenter] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const updateCenter = () => {
      if (cloudRef.current) {
        const rect = cloudRef.current.getBoundingClientRect();
        setCloudCenter({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      }
    };
    updateCenter();
    window.addEventListener('resize', updateCenter);
    return () => window.removeEventListener('resize', updateCenter);
  }, []);

  useEffect(() => {
    const tick = () => setFocusedIdx(Math.floor(Math.random() * 12));
    tick();
    const id = setInterval(tick, 4000);
    return () => clearInterval(id);
  }, []);

  const cardPosts = CARD_CONFIGS.map((_, i) => posts[i % posts.length] || null);

  return (
    <div ref={cloudRef} style={{ position: 'absolute', inset: 0 }}>
      {CARD_CONFIGS.map((config, i) => (
        <FloatingCard
          key={i}
          idx={i}
          config={config}
          cardWidth={CARD_WIDTHS[i]}
          post={cardPosts[i]}
          focused={focusedIdx === i}
          cloudCenter={cloudCenter}
        />
      ))}
    </div>
  );
}

export default function LoginClient({ nextUrl = '/', initialError = null }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loginState, loginAction, loginPending] = useActionState(login, { error: initialError });
  const [signupState, signupAction, signupPending] = useActionState(signup, {
    error: null,
    info: null,
  });
  const [resendState, resendAction, resendPending] = useActionState(resendVerification, null);

  const loading = mode === 'login' ? loginPending : signupPending;
  const error = mode === 'login' ? loginState?.error : signupState?.error;
  const info = mode === 'signup' ? signupState?.info : null;
  // Surface a "resend" button whenever Supabase says the email isn't
  // confirmed yet, or whenever signup returned the "check your email"
  // info message — both paths benefit from the same one-click affordance.
  const needsConfirmation =
    (mode === 'login' && typeof error === 'string' && /confirm/i.test(error)) ||
    (mode === 'signup' && !!info);

  // Floating-cloud posts. Falls back to empty if base44 isn't reachable.
  const { data: posts = [] } = useQuery({
    queryKey: ['auth-cloud-posts'],
    queryFn: () => base44.entities.Post.list('-created_date', 200),
    staleTime: 60000,
    retry: false,
  });

  const postsWithImages = useMemo(() => {
    const eligible = posts.filter((p) => {
      if (p.status && p.status !== 'published') return false;
      if (p.media_urls?.length > 0) return true;
      if (p.before_image || p.after_image) return true;
      if (p.post_type === 'text' || p.post_type === 'poll') return true;
      return false;
    });
    if (eligible.length === 0) return [];
    const shuffled = [...eligible].sort(() => Math.random() - 0.5);
    const authorSeen = new Set();
    const firstPass = [];
    const secondPass = [];
    for (const p of shuffled) {
      if (!authorSeen.has(p.author_email)) {
        authorSeen.add(p.author_email);
        firstPass.push(p);
      } else {
        secondPass.push(p);
      }
    }
    return [...firstPass, ...secondPass].slice(0, 24);
  }, [posts]);

  const switchMode = (newMode) => {
    setMode(newMode);
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <>
      <style>{`
        @keyframes drift0 { 0%,100%{transform:rotate(var(--r,0deg)) translate(0,0)} 33%{transform:rotate(calc(var(--r,0deg) + 0.5deg)) translate(12px,-14px)} 66%{transform:rotate(calc(var(--r,0deg) - 0.4deg)) translate(-8px,10px)} }
        @keyframes drift1 { 0%,100%{transform:rotate(var(--r,0deg)) translate(0,0)} 33%{transform:rotate(calc(var(--r,0deg) - 0.5deg)) translate(-15px,10px)} 66%{transform:rotate(calc(var(--r,0deg) + 0.3deg)) translate(10px,-12px)} }
        @keyframes drift2 { 0%,100%{transform:rotate(var(--r,0deg)) translate(0,0)} 33%{transform:rotate(calc(var(--r,0deg) + 0.4deg)) translate(8px,18px)} 66%{transform:rotate(calc(var(--r,0deg) - 0.5deg)) translate(-12px,-8px)} }
        @keyframes drift3 { 0%,100%{transform:rotate(var(--r,0deg)) translate(0,0)} 33%{transform:rotate(calc(var(--r,0deg) - 0.3deg)) translate(-10px,-16px)} 66%{transform:rotate(calc(var(--r,0deg) + 0.5deg)) translate(14px,6px)} }
        @keyframes drift4 { 0%,100%{transform:rotate(var(--r,0deg)) translate(0,0)} 33%{transform:rotate(calc(var(--r,0deg) + 0.5deg)) translate(18px,8px)} 66%{transform:rotate(calc(var(--r,0deg) - 0.3deg)) translate(-6px,-18px)} }
        @keyframes drift5 { 0%,100%{transform:rotate(var(--r,0deg)) translate(0,0)} 33%{transform:rotate(calc(var(--r,0deg) - 0.5deg)) translate(-18px,12px)} 66%{transform:rotate(calc(var(--r,0deg) + 0.4deg)) translate(10px,-10px)} }
      `}</style>

      <div style={{ minHeight: '100vh', display: 'flex', background: 'hsl(var(--background))' }}>
        {/* LEFT: Floating cloud */}
        <div
          className="hidden lg:flex"
          style={{ width: '50%', position: 'relative', overflow: 'hidden', flexShrink: 0 }}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'hsl(220 15% 5%)' }} />

          {postsWithImages.length > 0 && <FloatingCloud posts={postsWithImages} />}

          <div style={{ position: 'absolute', bottom: 48, left: 40, right: 40, zIndex: 50 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.15em',
                color: 'hsl(var(--primary))',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              Welcome to Octne —
            </p>
            <h2 style={{ fontSize: 36, fontWeight: 900, lineHeight: 1.1, color: '#fff', marginBottom: 12 }}>
              Where car<br />culture lives.
            </h2>
            <p
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.55)',
                lineHeight: 1.6,
                maxWidth: 280,
              }}
            >
              A platform built for builders, collectors, and enthusiasts. Document your rides, find
              your community, and join the conversation that&apos;s been missing from the internet.
            </p>
          </div>

          <div style={{ position: 'absolute', top: 28, left: 32, zIndex: 50 }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={LOGO} alt="Octne" style={{ height: 26 }} />
          </div>
        </div>

        {/* RIGHT: Form panel */}
        <div
          className="flex flex-col items-center justify-center w-full lg:w-1/2 px-6 py-12 sm:px-10"
          style={{ background: 'hsl(var(--background))', minHeight: '100vh' }}
        >
          <div style={{ width: '100%', maxWidth: 340 }}>
            {/* Mobile logo */}
            <div className="lg:hidden mb-8 flex justify-center">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={LOGO} alt="Octne" style={{ height: 26 }} />
            </div>

            {/* Mode toggle */}
            <div
              style={{
                display: 'flex',
                background: 'hsl(var(--secondary))',
                borderRadius: 10,
                padding: 4,
                gap: 4,
                marginBottom: 28,
              }}
            >
              <button
                type="button"
                onClick={() => switchMode('login')}
                style={{
                  flex: 1,
                  padding: '7px 0',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  background: mode === 'login' ? 'hsl(var(--card))' : 'transparent',
                  color:
                    mode === 'login' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: mode === 'login' ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => switchMode('signup')}
                style={{
                  flex: 1,
                  padding: '7px 0',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  background: mode === 'signup' ? 'hsl(var(--card))' : 'transparent',
                  color:
                    mode === 'signup' ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: mode === 'signup' ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                Create Account
              </button>
            </div>

            {/* LOGIN */}
            {mode === 'login' && (
              <form action={loginAction} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <input type="hidden" name="next" value={nextUrl} />
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Welcome back</h1>
                  <p style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))' }}>
                    Sign in to your Octne account
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <Label style={{ fontSize: 12, marginBottom: 6, display: 'block' }}>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground" />
                      <Input
                        type="email"
                        name="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-9"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 6,
                      }}
                    >
                      <Label style={{ fontSize: 12 }}>Password</Label>
                      <Link
                        href="/forgot-password"
                        style={{ fontSize: 11, color: 'hsl(var(--primary))', textDecoration: 'none', fontWeight: 600 }}
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-9 pr-9"
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {error && (
                  <p className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}

                {needsConfirmation && email && (
                  <form action={resendAction}>
                    <input type="hidden" name="email" value={email} />
                    {resendState?.info ? (
                      <p className="text-xs text-foreground bg-secondary border border-border rounded-lg px-3 py-2">
                        {resendState.info}
                      </p>
                    ) : (
                      <button
                        type="submit"
                        disabled={resendPending}
                        className="w-full text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                      >
                        {resendPending ? 'Sending…' : 'Resend confirmation email'}
                      </button>
                    )}
                    {resendState?.error && (
                      <p className="text-xs text-destructive mt-1.5">{resendState.error}</p>
                    )}
                  </form>
                )}

                <Button
                  type="submit"
                  className="w-full h-10 font-semibold rounded-xl gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>

                <p
                  style={{
                    fontSize: 12,
                    textAlign: 'center',
                    color: 'hsl(var(--muted-foreground))',
                  }}
                >
                  New to Octne?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signup')}
                    style={{
                      color: 'hsl(var(--primary))',
                      fontWeight: 600,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Create an account
                  </button>
                </p>
              </form>
            )}

            {/* SIGNUP */}
            {mode === 'signup' && (
              <form action={signupAction} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Join Octne</h1>
                  <p style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))' }}>
                    Create your free account
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <Label style={{ fontSize: 12, marginBottom: 6, display: 'block' }}>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground" />
                      <Input
                        type="email"
                        name="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-9"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label style={{ fontSize: 12, marginBottom: 6, display: 'block' }}>Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder="Min. 8 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-9 pr-9"
                        autoComplete="new-password"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label style={{ fontSize: 12, marginBottom: 6, display: 'block' }}>
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        placeholder="Re-enter password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-9"
                        autoComplete="new-password"
                        required
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <p className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
                {info && (
                  <p className="text-xs text-foreground bg-secondary border border-border rounded-lg px-3 py-2">
                    {info}
                  </p>
                )}

                {needsConfirmation && email && (
                  <form action={resendAction}>
                    <input type="hidden" name="email" value={email} />
                    {resendState?.info ? (
                      <p className="text-[11px] text-muted-foreground text-center">
                        {resendState.info}
                      </p>
                    ) : (
                      <button
                        type="submit"
                        disabled={resendPending}
                        className="w-full text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                      >
                        {resendPending ? 'Sending…' : 'Resend confirmation email'}
                      </button>
                    )}
                    {resendState?.error && (
                      <p className="text-xs text-destructive mt-1.5">{resendState.error}</p>
                    )}
                  </form>
                )}

                <Button
                  type="submit"
                  className="w-full h-10 font-semibold rounded-xl gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <span>Create Account</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>

                <p
                  style={{
                    fontSize: 11,
                    textAlign: 'center',
                    color: 'hsl(var(--muted-foreground))',
                  }}
                >
                  By signing up you agree to our{' '}
                  <Link href="/terms" style={{ color: 'hsl(var(--primary))' }}>
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link href="/privacy" style={{ color: 'hsl(var(--primary))' }}>
                    Privacy Policy
                  </Link>
                </p>

                <p
                  style={{
                    fontSize: 12,
                    textAlign: 'center',
                    color: 'hsl(var(--muted-foreground))',
                  }}
                >
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('login')}
                    style={{
                      color: 'hsl(var(--primary))',
                      fontWeight: 600,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Sign in
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
